import { Dispatch, SetStateAction, SyntheticEvent, useCallback, useMemo } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { CountryService } from '../../services/country';
import { BaseObj } from '../../types/baseObj';
import { ColumnBoxFlexFullWidth } from './boxes';
import { useGetCountryList } from '../../queryHooks/useGetCountryList';

interface IProps<T> {
  setState: Dispatch<SetStateAction<T>>,
  countryValue: string;
  townValue: string;
  validationErrors: { [key: string]: string | null },
  disabled?: boolean,
  variant?: 'standard' | 'outlined' | 'filled',
}

const SelectCountryAndTown = <T extends BaseObj>({
                                                   setState,
                                                   countryValue,
                                                   townValue,
                                                   validationErrors,
                                                   disabled = false,
                                                   variant
                                                 }: IProps<T>) => {
  const { data: countriesList } = useGetCountryList();
  const countryList = useMemo(() => countriesList || [], [countriesList]);
  const selectedCountry = useMemo(() => countryList?.find(d => d.countryISO3code === countryValue) || null,
    [countryList, countryValue]);

  const { data: country, isLoading } = useQuery({
    queryKey: ['country', selectedCountry?.countryISO3code || ''],
    queryFn: () => CountryService.getEntity(selectedCountry?.countryISO3code || ''),
    enabled: !!selectedCountry,
    refetchOnWindowFocus: false,
    cacheTime: 0
  });

  // Map is used to filter out the duplicated town names. I found many name duplications for towns in Brazil.
  const townsList = useMemo(() => country ? [...new Map(country.cities.map(c => [c.name, c])).values()] : [], [country]);
  const selectedTown = useMemo(() => (townValue ? { name: townValue } : null), [townValue]);


  const handleCountryChange = useCallback((e: SyntheticEvent<Element, Event>) => {
    if ((e as any)?.code !== 'Enter') {
      const name = e.currentTarget.textContent?.trim();
      const selectedCountry = countryList.find(c => c.name === name);
      setState(prev => ({
        ...prev,
        country: selectedCountry?.countryISO3code || '',
        town: ''
      }));
    }
  }, [countryList, setState]);

  const handleTownChange = useCallback((e: SyntheticEvent<Element, Event>) => {
    if ((e as any)?.code !== 'Enter') {
      setState(prev => ({
          ...prev,
          town: e.currentTarget.textContent?.trim() || ''
        })
      );
    }
  }, [setState]);


  const handleCountryPressEnter = useCallback((e: SyntheticEvent<Element, Event>, val: string) => {
    if ((e as any)?.code === 'Enter') {
      const selectedCountry = countryList.find(c => c.name === val.trim());
      setState(prev => ({
        ...prev,
        country: selectedCountry?.countryISO3code || '',
        town: ''
      }));
    }
  }, [countryList, setState]);

  const handleTownPressEnter = useCallback((e: SyntheticEvent<Element, Event>, val: string) => {
    if ((e as any)?.code === 'Enter') {
      setState(prev => ({
          ...prev,
          town: val.trim() || ''
        })
      );
    }
  }, [setState]);

  return (
    <ColumnBoxFlexFullWidth position={'relative'} mt={2}>
      <Autocomplete
        getOptionLabel={(option) => option.name}
        onInputChange={handleCountryPressEnter}
        onChange={handleCountryChange}
        blurOnSelect={true}
        disabled={disabled}
        loading={isLoading}
        value={selectedCountry}
        renderOption={(props, option) => (
          <Box component={'li'} sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
            <img
              loading={'lazy'}
              width={20}
              src={`https://flagcdn.com/w20/${option.countryISO2code.toLowerCase()}.png`}
              srcSet={`https://flagcdn.com/w40/${option.countryISO2code.toLowerCase()}.png 2x`}
              alt={option.countryISO3code}
            />
            {option.name}
          </Box>
        )}
        renderInput={(params) => (
          <TextField {...params}
                     label={'Country'}
                     error={!!validationErrors['country']}
                     inputProps={{ ...params.inputProps, autoComplete: 'none', }}
                     helperText={validationErrors['country']}
                     required
                     variant={variant || disabled ? 'standard' : 'outlined'}
          />
        )}
        options={countryList}
      />

      <Box mt={2}>
        <Autocomplete
          options={townsList}
          getOptionLabel={(option) => option.name}
          disabled={!selectedCountry || !townsList.length || disabled}
          loading={isLoading}
          blurOnSelect={true}
          onInputChange={handleTownPressEnter}
          onChange={handleTownChange}
          isOptionEqualToValue={(option, value) => option.name === value.name}
          value={selectedTown}
          renderOption={(props, option) => (
            <Typography {...props}> {option.name} </Typography>
          )}
          renderInput={(params) => (
            <TextField {...params}
                       label={'Town'}
                       inputProps={{ ...params.inputProps, autoComplete: 'none' }}
                       error={!!validationErrors['town']}
                       helperText={validationErrors['town']}
                       required
                       variant={variant || disabled ? 'standard' : 'outlined'}
            />
          )}
        />
      </Box>

    </ColumnBoxFlexFullWidth>
  );
};

export default SelectCountryAndTown;